import { t } from "@lingui/macro"
import { Alert, Button } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback } from "react"

import { Form } from "src/components/Form"
import { ModalHeader } from "src/components/ModalHeader"
import { KnowledgeItemsContentSection } from "src/modals/knowledge-items/sections/Content"
import { KnowledgeItemsSettingsSection } from "src/modals/knowledge-items/sections/Settings"
import { KnowledgeItemsPublishSettingsSection } from "src/modals/knowledge-items/sections/PublishSettings"
import { KnowledgeItemStore } from "src/modals/knowledge-items/store"
import { IKnowledgeItemModalProps } from "src/modals/knowledge-items/types/knowledgeItemProps"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { FormPanel } from "src/components/FormPanel"
import { useInitializer } from "src/lib/initializer"
import { CenteredSpinner } from "src/components/CenteredSpinner"
import { IKnowledgeItemFormFields } from "src/modals/knowledge-items/types/formFields"
import { useCloseConfirmationForForm } from "src/store/modals/use-close-confirmation-for-form"
import { IFormFieldValidation } from "src/types/form-fields/validation"
import { DEFAULT_ACCESS_GROUP } from "src/config"

const Modal = observer((props: IKnowledgeItemModalProps) => {
    const store = useStore(KnowledgeItemStore)
    const globalStore = useStore(GlobalStore)
    const type = store.formFields.get("knowledge_item_type")
    const shouldShowPublishSettings = store.shouldShowPublishSettings()

    useCloseConfirmationForForm(store.formFields)

    const initialized = useInitializer(async () => {
        await store.init(
            globalStore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id,
            props.id,
            props.mode,
            props.preselectedValues,
        )
    }, [
        globalStore.session.accessGroup,
        props.id,
        props.mode,
        props.preselectedValues,
    ])

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            store.setIsLoading(true)

            // Validate required fields
            const requiredFields: IFormFieldValidation<IKnowledgeItemFormFields>[] =
                [
                    { field: "access_group_id" },
                    { field: "knowledge_item_type" },
                    { field: "title" },
                ]

            // Add content validation based on type
            if (type === "file") {
                requiredFields.push({
                    field: "content",
                    validate: (value) => value != null && value !== "",
                    errorMessage: t`knowledge-item-modal.content.file-required`,
                })
            } else if (type === "page") {
                requiredFields.push({
                    field: "content",
                    validate: (value) => value != null && value !== "",
                    errorMessage: t`knowledge-item-modal.content.page-required`,
                })
            } else if (type === "web") {
                requiredFields.push({
                    field: "content",
                    validate: (value) => value != null && value !== "",
                    errorMessage: t`knowledge-item-modal.content.web-search-url-required`,
                })
            }

            store.formFields.validateRequiredFields(requiredFields)

            // Validate agent instances
            const agentInstanceError = store.validateRequiredAgentInstances()
            if (agentInstanceError !== null) {
                store.formFields.setError("generic", agentInstanceError)
            }

            if (store.formFields.hasErrors() === true) {
                store.setIsLoading(false)
                return
            }

            // Track analytics if needed

            const success = await store.submit()

            if (success) {
                globalStore.modals.pop()
            }
        },
        [globalStore.modals, store, type],
    )

    if (!initialized) {
        return <CenteredSpinner height="100vh" />
    }

    const isButtonDisabled =
        store.isReadOnly ||
        store.formFields.getIsDirty() === false ||
        store.isLoading

    return (
        <Form onSubmit={handleSubmit}>
            <>
                <ModalHeader>
                    <Button
                        type="submit"
                        variant="contained"
                        color="error"
                        disabled={isButtonDisabled}
                        data-testid="submit-item-form"
                    >
                        {t`global.save`}
                    </Button>
                </ModalHeader>
                {Boolean(store.formFields.error("generic")) && (
                    <Alert severity="error">
                        {store.formFields.error("generic")}
                    </Alert>
                )}
                <FormPanel
                    sections={[
                        {
                            header: t`knowledge-item-modal.settings`,
                            content: <KnowledgeItemsSettingsSection />,
                        },
                    ]}
                />
                {type != null && (
                    <>
                        <FormPanel
                            sections={[
                                {
                                    header: t`knowledge-item-modal.content`,
                                    content: <KnowledgeItemsContentSection />,
                                },
                            ]}
                        />

                        {shouldShowPublishSettings && (
                            <FormPanel
                                sections={[
                                    {
                                        header: t`knowledge-item-modal.publish-settings`,
                                        content: (
                                            <KnowledgeItemsPublishSettingsSection />
                                        ),
                                    },
                                ]}
                            />
                        )}
                    </>
                )}
            </>
        </Form>
    )
})

export const KnowledgeItemModal = (props: IKnowledgeItemModalProps) => (
    <StoreProvider Store={KnowledgeItemStore}>
        <Modal {...props} />
    </StoreProvider>
)
